import type { StudioFaceLandmarks } from './studioImageFaceAnalysis'

export interface StudioBeautyMasks {
  face: HTMLCanvasElement
  eyes: HTMLCanvasElement
  lips: HTMLCanvasElement
  underEyes: HTMLCanvasElement
  teeth: HTMLCanvasElement
  blemishes: HTMLCanvasElement
}

const contours = {
  face: [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109],
  leftEye: [33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246],
  rightEye: [362, 382, 381, 380, 374, 373, 390, 249, 263, 466, 388, 387, 386, 385, 384, 398],
  leftUnderEye: [33, 7, 163, 144, 145, 153, 154, 155, 133, 244, 233, 232, 231, 230, 229, 228, 31, 226, 130],
  rightUnderEye: [362, 382, 381, 380, 374, 373, 390, 249, 263, 446, 261, 448, 449, 450, 451, 452, 453, 464],
  lipsOuter: [61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146],
  upperLip: [61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 308, 415, 310, 311, 312, 13, 82, 81, 80, 191, 78],
  lowerLip: [61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 308, 324, 318, 402, 317, 14, 87, 178, 88, 95, 78],
  teeth: [78, 95, 88, 178, 87, 14, 317, 402, 318, 324, 308, 13, 312, 311, 310, 415, 308],
} as const

const createMask = (width: number, height: number) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

const drawPolygon = (
  context: CanvasRenderingContext2D,
  face: StudioFaceLandmarks[number],
  indices: readonly number[],
  width: number,
  height: number,
  fill: string,
) => {
  const first = face[indices[0] ?? -1]
  if (!first) return
  context.beginPath()
  context.moveTo(first.x * width, first.y * height)
  for (let index = 1; index < indices.length; index += 1) {
    const point = face[indices[index] ?? -1]
    if (point) context.lineTo(point.x * width, point.y * height)
  }
  context.closePath()
  context.fillStyle = fill
  context.fill()
}

const drawEyeApertureAndIris = (
  context: CanvasRenderingContext2D,
  face: StudioFaceLandmarks[number],
  eyeIndices: readonly number[],
  irisCenterIndex: number,
  irisEdgeIndex: number,
  width: number,
  height: number,
) => {
  const points = eyeIndices.map((i) => face[i]).filter((p): p is NonNullable<typeof p> => Boolean(p))
  if (points.length < 4) return

  // 1. Draw Sclera (Eye White) in Green (channel G) across eye aperture
  context.save()
  context.beginPath()
  context.moveTo(points[0]!.x * width, points[0]!.y * height)
  for (let i = 1; i < points.length; i += 1) {
    context.lineTo(points[i]!.x * width, points[i]!.y * height)
  }
  context.closePath()
  context.fillStyle = 'rgb(0, 255, 0)'
  context.fill()

  // 2. Clip strictly inside eye opening so iris never spills outside eyelids
  context.clip()

  // 3. Determine iris center and radius
  let cx = 0
  let cy = 0
  let radius = 0

  if (face[irisCenterIndex] && face[irisEdgeIndex]) {
    cx = face[irisCenterIndex]!.x * width
    cy = face[irisCenterIndex]!.y * height
    radius = Math.hypot(
      (face[irisEdgeIndex]!.x - face[irisCenterIndex]!.x) * width,
      (face[irisEdgeIndex]!.y - face[irisCenterIndex]!.y) * height,
    )
  } else {
    const xs = points.map((p) => p.x * width)
    const ys = points.map((p) => p.y * height)
    cx = (Math.min(...xs) + Math.max(...xs)) / 2
    cy = (Math.min(...ys) + Math.max(...ys)) / 2
    radius = (Math.max(...xs) - Math.min(...xs)) * 0.28
  }

  // 4. Draw Iris in Red (channel R) - source-over naturally replaces Green with Red in the iris
  context.beginPath()
  context.arc(cx, cy, Math.max(1, radius), 0, Math.PI * 2)
  context.fillStyle = 'rgb(255, 0, 0)'
  context.fill()

  context.restore()
}

const drawUnderEyeRegion = (
  context: CanvasRenderingContext2D,
  face: StudioFaceLandmarks[number],
  indices: readonly number[],
  fallbackEyeIndices: readonly number[],
  width: number,
  height: number,
) => {
  const points = indices.map((index) => face[index]).filter((point): point is NonNullable<typeof point> => Boolean(point))
  if (points.length >= 6) {
    context.beginPath()
    context.moveTo(points[0]!.x * width, points[0]!.y * height)
    for (let index = 1; index < points.length; index += 1) {
      context.lineTo(points[index]!.x * width, points[index]!.y * height)
    }
    context.closePath()
    context.fillStyle = '#fff'
    context.fill()
    return
  }

  // Compact crescent fallback if dense landmarks aren't available (e.g. test mock)
  const eye = fallbackEyeIndices.map((index) => face[index]).filter((point): point is NonNullable<typeof point> => Boolean(point))
  if (eye.length < 4) return
  const xs = eye.map(({ x }) => x)
  const ys = eye.map(({ y }) => y)
  const eyeWidth = Math.max(...xs) - Math.min(...xs)
  const eyeHeight = Math.max(...ys) - Math.min(...ys)
  const centerX = (Math.min(...xs) + Math.max(...xs)) / 2
  const centerY = Math.max(...ys) + eyeHeight * 0.28
  const radiusX = eyeWidth * 0.45
  const radiusY = Math.max(eyeHeight * 0.32, eyeWidth * 0.16)
  context.beginPath()
  context.ellipse(centerX * width, centerY * height, radiusX * width, radiusY * height, 0, 0, Math.PI * 2)
  context.fillStyle = '#fff'
  context.fill()
}

const blurAlpha = (input: Uint8ClampedArray, width: number, height: number, radius: number) => {
  const horizontal = new Uint8ClampedArray(input.length)
  const output = new Uint8ClampedArray(input.length)
  const diameter = radius * 2 + 1
  for (let y = 0; y < height; y += 1) {
    const row = y * width
    let sum = 0
    for (let offset = -radius; offset <= radius; offset += 1) {
      sum += input[row + Math.max(0, Math.min(width - 1, offset))]!
    }
    for (let x = 0; x < width; x += 1) {
      horizontal[row + x] = Math.round(sum / diameter)
      sum += input[row + Math.min(width - 1, x + radius + 1)]!
        - input[row + Math.max(0, x - radius)]!
    }
  }
  for (let x = 0; x < width; x += 1) {
    let sum = 0
    for (let offset = -radius; offset <= radius; offset += 1) {
      sum += horizontal[Math.max(0, Math.min(height - 1, offset)) * width + x]!
    }
    for (let y = 0; y < height; y += 1) {
      output[y * width + x] = Math.round(sum / diameter)
      sum += horizontal[Math.min(height - 1, y + radius + 1) * width + x]!
        - horizontal[Math.max(0, y - radius) * width + x]!
    }
  }
  return output
}

const featherEyeMask = (canvas: HTMLCanvasElement, radius: number) => {
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return
  const image = context.getImageData(0, 0, canvas.width, canvas.height)
  const red = new Uint8ClampedArray(canvas.width * canvas.height)
  const green = new Uint8ClampedArray(canvas.width * canvas.height)
  for (let pixel = 0; pixel < red.length; pixel += 1) {
    red[pixel] = image.data[pixel * 4]!
    green[pixel] = image.data[pixel * 4 + 1]!
  }
  const passRadius = Math.max(1, Math.round(radius / 2))
  const softenedRed = blurAlpha(blurAlpha(red, canvas.width, canvas.height, passRadius), canvas.width, canvas.height, passRadius)
  const softenedGreen = blurAlpha(blurAlpha(green, canvas.width, canvas.height, passRadius), canvas.width, canvas.height, passRadius)
  for (let pixel = 0; pixel < red.length; pixel += 1) {
    const index = pixel * 4
    image.data[index] = softenedRed[pixel]!
    image.data[index + 1] = softenedGreen[pixel]!
    image.data[index + 2] = 0
    image.data[index + 3] = Math.max(softenedRed[pixel]!, softenedGreen[pixel]!)
  }
  context.putImageData(image, 0, 0)
}

const featherMask = (canvas: HTMLCanvasElement, radius: number) => {
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return
  const image = context.getImageData(0, 0, canvas.width, canvas.height)
  const alpha = new Uint8ClampedArray(canvas.width * canvas.height)
  for (let pixel = 0; pixel < alpha.length; pixel += 1) alpha[pixel] = image.data[pixel * 4 + 3]!
  const passRadius = Math.max(1, Math.round(radius / 2))
  const softened = blurAlpha(blurAlpha(alpha, canvas.width, canvas.height, passRadius), canvas.width, canvas.height, passRadius)
  for (let pixel = 0; pixel < alpha.length; pixel += 1) {
    const index = pixel * 4
    image.data[index] = 255
    image.data[index + 1] = 255
    image.data[index + 2] = 255
    image.data[index + 3] = softened[pixel]!
  }
  context.putImageData(image, 0, 0)
}

export const createStudioBeautyMasks = (width: number, height: number, faces: StudioFaceLandmarks): StudioBeautyMasks => {
  const maskCanvases = {
    face: createMask(width, height),
    eyes: createMask(width, height),
    lips: createMask(width, height),
    underEyes: createMask(width, height),
    teeth: createMask(width, height),
    blemishes: createMask(width, height),
  }
  const contexts = Object.fromEntries(
    Object.entries(maskCanvases).map(([key, canvas]) => [key, canvas.getContext('2d')]),
  ) as Record<keyof StudioBeautyMasks, CanvasRenderingContext2D | null>

  if (!contexts.face || !contexts.eyes || !contexts.underEyes || !contexts.lips || !contexts.teeth || !contexts.blemishes) {
    return maskCanvases
  }

  if (faces.length === 0) {
    // Fallback: graceful oval covering central portrait region so beauty adjustments never fail
    contexts.face.fillStyle = '#fff'
    contexts.face.beginPath()
    contexts.face.ellipse(width * 0.5, height * 0.44, width * 0.32, height * 0.38, 0, 0, Math.PI * 2)
    contexts.face.fill()
    featherMask(maskCanvases.face, Math.max(16, Math.min(80, width * 0.15)))
    return maskCanvases
  }

  for (const face of faces) {
    drawPolygon(contexts.face, face, contours.face, width, height, '#fff')
    drawEyeApertureAndIris(contexts.eyes, face, contours.leftEye, 468, 469, width, height)
    drawEyeApertureAndIris(contexts.eyes, face, contours.rightEye, 473, 474, width, height)
    drawPolygon(contexts.lips, face, contours.lipsOuter, width, height, '#fff')
    drawPolygon(contexts.lips, face, contours.upperLip, width, height, '#fff')
    drawPolygon(contexts.lips, face, contours.lowerLip, width, height, '#fff')
    drawPolygon(contexts.teeth, face, contours.teeth, width, height, '#fff')
    contexts.underEyes.fillStyle = '#fff'
    drawUnderEyeRegion(contexts.underEyes, face, contours.leftUnderEye, contours.leftEye, width, height)
    drawUnderEyeRegion(contexts.underEyes, face, contours.rightUnderEye, contours.rightEye, width, height)
  }

  const faceWidth = Math.max(width * .15, ...faces.map((face) => {
    const xs = contours.face.map((index) => face[index]?.x).filter((value): value is number => typeof value === 'number')
    return xs.length ? (Math.max(...xs) - Math.min(...xs)) * width : 0
  }))
  featherMask(maskCanvases.face, Math.max(8, Math.min(64, faceWidth * .10)))
  featherEyeMask(maskCanvases.eyes, Math.max(2, Math.min(6, faceWidth * .012)))
  featherMask(maskCanvases.underEyes, Math.max(4, Math.min(18, faceWidth * .035)))
  featherMask(maskCanvases.lips, Math.max(2, Math.min(8, faceWidth * .016)))
  featherMask(maskCanvases.teeth, Math.max(2, Math.min(6, faceWidth * .012)))

  return maskCanvases
}
